import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { RefreshCw, AlertCircle, Search } from 'lucide-react';
import { API_URL } from '../config';

const PublicVitrinePage = ({ username }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [user, setUser] = useState(null);
    const [posts, setPosts] = useState([]);
    const [search, setSearch] = useState('');
    const [selectedStore, setSelectedStore] = useState('all');

    useEffect(() => {
        carregarVitrine();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [username]);

    const carregarVitrine = async () => {
        setLoading(true);
        setError(null);
        try {
            // Requisição para a rota da API com json=true
            const res = await axios.get(`${API_URL}/vitrine/${username}?json=true`);
            setUser(res.data.user);
            setPosts(res.data.posts);
        } catch (err) {
            console.error("Erro ao carregar vitrine:", err);
            setError(err.response?.data?.error || "Perfil não encontrado ou erro ao buscar dados da vitrine.");
        } finally {
            setLoading(false);
        }
    };

    function getStoreId(loja) {
        const l = String(loja).toLowerCase();
        if (l.includes('shopee')) return 'shopee';
        if (l.includes('aliexpress')) return 'aliexpress';
        if (l.includes('shein')) return 'shein';
        if (l.includes('amazon')) return 'amazon';
        if (l.includes('mercado') || l.includes('meli') || l === 'ml') return 'ml';
        if (l.includes('magazine') || l.includes('luiza') || l.includes('magalu')) return 'magalu';
        if (l.includes('tiktok')) return 'tiktok';
        return 'outros';
    }

    function getStoreConfig(loja) {
        const id = getStoreId(loja);
        switch (id) {
            case 'shopee': return { name: 'Shopee', color: '#ee4d2d', bg: '#fef2f2' };
            case 'aliexpress': return { name: 'AliExpress', color: '#e62e04', bg: '#fff1f0' };
            case 'shein': return { name: 'Shein', color: '#1e293b', bg: '#f8fafc' };
            case 'amazon': return { name: 'Amazon', color: '#146eb4', bg: '#f0f9ff' };
            case 'ml': return { name: 'Mercado Livre', color: '#2d3277', bg: '#fefcbf' };
            case 'magalu': return { name: 'Magazine Luiza', color: '#0086ff', bg: '#f0fdf4' };
            case 'tiktok': return { name: 'TikTok Shop', color: '#fe2c55', bg: '#fff1f2' };
            default: return { name: loja || 'Outros', color: '#64748b', bg: '#f1f5f9' };
        }
    }

    if (loading) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
                <RefreshCw size={40} className="animate-spin" style={{ color: '#ee4d2d', animation: 'spin 1s linear infinite' }} />
                <p style={{ marginTop: '16px', color: '#64748b', fontSize: '15px', fontWeight: '600', fontFamily: 'Inter, sans-serif' }}>Carregando Vitrine...</p>
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    if (error || !user) {
        const isForbidden = error && typeof error === 'string' && error.includes('plano');
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#f8fafc', padding: '20px', fontFamily: 'Inter, sans-serif' }}>
                <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '16px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)', maxWidth: '400px', width: '100%', textAlign: 'center' }}>
                    <AlertCircle size={48} style={{ color: isForbidden ? '#eab308' : '#ef4444', margin: '0 auto 16px' }} />
                    <h2 style={{ color: '#0f172a', fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>
                        {isForbidden ? 'Vitrine Inativa 🔒' : 'Perfil não encontrado'}
                    </h2>
                    <p style={{ color: '#64748b', fontSize: '14px', lineHeight: '1.5' }}>
                        {isForbidden ? error : `A vitrine para o usuário "@${username}" não foi encontrada no nosso sistema. Verifique o link e tente novamente.`}
                    </p>
                </div>
            </div>
        );
    }

    const activeStores = new Set(posts.map(post => getStoreId(post.loja)));
    const storeOrder = ['shopee', 'aliexpress', 'shein', 'amazon', 'ml', 'magalu', 'tiktok', 'outros'];

    // Filtro dos posts baseado na busca (nome e ref) e na plataforma selecionada
    const cleanSearch = search.toLowerCase().trim().replace('#', '');
    const filteredPosts = posts.filter(post => {
        const nameMatches = post.productName.toLowerCase().includes(search.toLowerCase());
        const refMatches = String(post.id) === cleanSearch || String(post.id).includes(cleanSearch);
        const matchesSearch = nameMatches || refMatches;

        const storeId = getStoreId(post.loja);
        const matchesStore = selectedStore === 'all' || storeId === selectedStore;

        return matchesSearch && matchesStore;
    });

    const fallbackImage = 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=500&auto=format&fit=crop&q=60';
    const displayName = user.instagramUsername || user.username;
    const hasAvatar = user.picture && !user.picture.includes('images.unsplash.com/photo-1535713875002-d1d0cf377fde');

    return (
        <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh', paddingBottom: '60px', fontFamily: 'Inter, sans-serif' }}>
            {/* Header */}
            <div style={{ textAlign: 'center', padding: '40px 20px 25px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                {hasAvatar && (
                    <img
                        src={`${API_URL}/vitrine/avatar/${username}`}
                        alt="Avatar"
                        style={{ width: '90px', height: '90px', borderRadius: '50%', objectFit: 'cover', border: '4px solid #fff', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                )}
                <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', letterSpacing: '-0.02em', margin: 0 }}>@{displayName}</h2>
                <p style={{ fontSize: '14px', color: '#64748b', maxWidth: '400px', lineHeight: '1.5', margin: 0 }}>
                    Seja bem-vindo(a) à minha vitrine oficial de achadinhos! Selecione o produto abaixo para aproveitar as melhores promoções da internet de forma segura. 🛍️✨
                </p>
            </div>

            {/* Caixa de Busca */}
            <div style={{ width: '100%', maxWidth: '500px', margin: '0 auto 20px', padding: '0 20px', position: 'relative', boxSizing: 'border-box' }}>
                <input
                    type="text"
                    placeholder="Buscar produto por nome ou código Ref..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{
                        width: '100%', padding: '14px 20px 14px 44px', borderRadius: '25px', border: '1px solid #e2e8f0',
                        backgroundColor: '#fff', fontSize: '15px', fontWeight: '500', outline: 'none',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)', boxSizing: 'border-box', transition: 'all 0.2s'
                    }}
                />
                <Search size={18} style={{ position: 'absolute', left: '36px', top: '16px', color: '#94a3b8' }} />
            </div>

            {/* Filtros de Plataforma */}
            {activeStores.size > 1 && (
                <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', padding: '5px 20px 20px', maxWidth: '800px', margin: '0 auto', scrollbarWidth: 'none', boxSizing: 'border-box' }}>
                    <button
                        onClick={() => setSelectedStore('all')}
                        style={{
                            padding: '8px 16px', borderRadius: '20px', border: '1px solid #e2e8f0',
                            backgroundColor: selectedStore === 'all' ? '#ee4d2d' : '#fff',
                            color: selectedStore === 'all' ? '#fff' : '#64748b',
                            fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s'
                        }}
                    >
                        Todos
                    </button>
                    {storeOrder.map(storeId => {
                        if (activeStores.has(storeId)) {
                            const config = getStoreConfig(storeId);
                            const active = selectedStore === storeId;
                            return (
                                <button
                                    key={storeId}
                                    onClick={() => setSelectedStore(storeId)}
                                    style={{
                                        padding: '8px 16px', borderRadius: '20px', border: '1px solid #e2e8f0',
                                        backgroundColor: active ? '#ee4d2d' : '#fff',
                                        color: active ? '#fff' : '#64748b',
                                        fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s'
                                    }}
                                >
                                    {config.name}
                                </button>
                            );
                        }
                        return null;
                    })}
                </div>
            )}

            {/* Grid de Produtos */}
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px', boxSizing: 'border-box' }}>
                {filteredPosts.length > 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '24px' }}>
                        {filteredPosts.map(post => {
                            const store = getStoreConfig(post.loja);
                            const imgUrl = post.imageUrl || fallbackImage;
                            return (
                                <div
                                    key={post.id}
                                    style={{
                                        backgroundColor: '#fff', borderRadius: '16px', overflow: 'hidden', border: '1px solid #e2e8f0',
                                        display: 'flex', flexDirection: 'column', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02)',
                                        transition: 'transform 0.2s, box-shadow 0.2s'
                                    }}
                                    className="vitrine-card"
                                >
                                    <div style={{ position: 'relative', width: '100%', paddingTop: '100%', overflow: 'hidden', backgroundColor: '#f8fafc' }}>
                                        <span style={{ position: 'absolute', top: '12px', left: '12px', padding: '4px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: '800', backgroundColor: 'rgba(15, 23, 42, 0.75)', color: '#fff', backdropFilter: 'blur(4px)', zIndex: 2 }}>
                                            Ref: #{post.id}
                                        </span>
                                        <img
                                            src={imgUrl}
                                            alt={post.productName}
                                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                                            onError={(e) => { e.target.src = fallbackImage; }}
                                        />
                                        <span style={{ position: 'absolute', top: '12px', right: '12px', padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', backgroundColor: store.bg, color: store.color, border: `1px solid ${store.color}22` }}>
                                            {store.name}
                                        </span>
                                    </div>
                                    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', flex: 1, gap: '12px' }}>
                                        <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#0f172a', lineHeight: '1.4', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', height: '42px', margin: 0 }}>
                                            {post.productName}
                                        </h3>
                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                            <span style={{ fontSize: '20px', fontWeight: '700', color: '#ee4d2d' }}>R$ {post.price}</span>
                                        </div>
                                        <a
                                            href={post.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ display: 'block', width: '100%', padding: '12px', textAlign: 'center', borderRadius: '10px', backgroundColor: '#ee4d2d', color: '#fff', fontSize: '14px', fontWeight: '700', textDecoration: 'none', marginTop: 'auto', boxShadow: '0 4px 6px -1px rgba(238, 77, 45, 0.15)', transition: 'opacity 0.2s' }}
                                        >
                                            Ir para Oferta 🛒
                                        </a>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '80px 20px', color: '#64748b' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#0f172a' }}>Nenhum produto encontrado</h3>
                        <p style={{ fontSize: '14px', margin: 0 }}>Tente buscar com outro termo ou código Ref.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PublicVitrinePage;
